import { EyeIcon } from "@heroicons/react/24/outline";
import type { MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { EvidenceFocus } from "../../../annotation/textEvidence";
import { AskInChatButton, SelectionToChat } from "../../../chatReferences";
import type { Individual, ShownStatement } from "../../../knowledgeData/abox/aboxViewModel";
import StatementSummary from "../../../knowledgeData/abox/StatementSummary";
import IconButton from "../../../../ui/buttons/IconButton";
import { classNames } from "../../../../ui/styles/classNames";
import { itemKeys, type PlacedStatement } from "./regestParts";

interface PartStatementsProps {
  /** The part's statements, each with those it shows by a value (`absorbNamedIndividuals`). */
  readonly items: readonly ShownStatement<PlacedStatement>[];
  readonly individuals: readonly Individual[];
  readonly focus: EvidenceFocus;
  /** Where selecting a statement's words asks the agent about its result (F96); `undefined` without a project. */
  readonly askInProject: string | undefined;
  /** What a statement's line also shows. */
  readonly extra: ((statement: PlacedStatement) => ReactNode) | undefined;
}

/**
 * What the data says about one part of the text, beside it (O-1): each statement once, compact.
 * A statement not accepted, such as a proposal under review, is drawn apart from accepted ones
 * (F154). Pointing at one highlights its words in the text; its button, or a click on the line,
 * keeps them highlighted, and a kept statement offers to ask about its result (F96).
 * `data-statements` lets a click on the words find it again.
 */
export default function PartStatements({ items, individuals, focus, askInProject, extra }: PartStatementsProps) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-1 text-sm">
      {items.map((item) => {
        const keys = itemKeys(item);
        const highlighted = keys.some((key) => focus.highlighted.has(key));
        const kept = keys.some((key) => focus.selected.has(key));
        const summary = <StatementSummary statement={item.shown.statement} individuals={individuals} modelCommit={item.shown.modelCommit} />;
        const subject = { reference: item.shown.source, name: t("chatReferences.statements") };
        // A click on the line keeps it; one on a term or an action inside it does only that.
        const keepOnClick = (event: MouseEvent) => {
          if (event.target instanceof Element && event.target.closest("a, button") !== null) return;
          focus.onSelect(keys);
        };
        return (
          <li
            key={item.shown.key}
            data-statements={keys.join(" ")}
            onMouseEnter={() => { focus.onPoint(keys); }}
            onMouseLeave={() => { focus.onPoint(null); }}
            onClick={keepOnClick}
            className={classNames("flex cursor-pointer items-start gap-2 rounded px-2 py-1", !item.shown.accepted && "app-statement-proposed", highlighted && "app-evidence-focused")}
          >
            <div className="min-w-0 flex-1 space-y-1">
              {item.shown.accepted ? null : <span className="sr-only">{t("sources.document.regest.proposed")}</span>}
              {askInProject === undefined ? summary : <SelectionToChat projectId={askInProject} subject={subject}>{summary}</SelectionToChat>}
              {extra?.(item.shown)}
              {kept && askInProject !== undefined ? <AskInChatButton projectId={askInProject} subject={subject} /> : null}
            </div>
            <IconButton
              label={t("sources.document.added.showInText")}
              title={t("sources.document.added.showInText")}
              aria-pressed={kept}
              onClick={() => { focus.onSelect(keys); }}
              onFocus={() => { focus.onPoint(keys); }}
              onBlur={() => { focus.onPoint(null); }}
              className="h-7 w-7 shrink-0"
            >
              <EyeIcon className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </li>
        );
      })}
    </ul>
  );
}
